(() => {
  'use strict';

  const PLAYER_SELECTOR = '[data-audio-player]';
  const YOUTUBE_API_URL = 'https://www.youtube.com/iframe_api';

  let youtubeApiPromise = null;

  function loadYouTubeApi() {
    if (window.YT && typeof window.YT.Player === 'function') {
      return Promise.resolve(window.YT);
    }

    if (youtubeApiPromise) {
      return youtubeApiPromise;
    }

    youtubeApiPromise = new Promise((resolve, reject) => {
      const previousCallback = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousCallback === 'function') {
          previousCallback();
        }

        resolve(window.YT);
      };

      const existingScript = document.querySelector(
        `script[src="${YOUTUBE_API_URL}"]`
      );

      if (existingScript) {
        existingScript.addEventListener(
          'error',
          () => reject(new Error('YouTube APIの読み込みに失敗しました。')),
          { once: true }
        );

        return;
      }

      const script = document.createElement('script');
      script.src = YOUTUBE_API_URL;
      script.async = true;
      script.onerror = () => {
        reject(new Error('YouTube APIの読み込みに失敗しました。'));
      };

      document.head.appendChild(script);
    });

    return youtubeApiPromise;
  }

  function formatDuration(totalSeconds) {
    const secondsNumber = Number(totalSeconds);

    if (!Number.isFinite(secondsNumber) || secondsNumber <= 0) {
      return '--:--';
    }

    const roundedSeconds = Math.floor(secondsNumber);
    const hours = Math.floor(roundedSeconds / 3600);
    const minutes = Math.floor((roundedSeconds % 3600) / 60);
    const seconds = roundedSeconds % 60;

    if (hours > 0) {
      return [
        hours,
        String(minutes).padStart(2, '0'),
        String(seconds).padStart(2, '0')
      ].join(':');
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  class CustomAudioPlayer {
    constructor(element) {
      this.element = element;
      this.playerTarget = element.querySelector('[data-youtube-player]');
      this.tracks = Array.from(element.querySelectorAll('[data-track]'));

      this.previousButton = element.querySelector('[data-player-previous]');
      this.toggleButton = element.querySelector('[data-player-toggle]');
      this.nextButton = element.querySelector('[data-player-next]');
      this.playlistToggle = element.querySelector('[data-playlist-toggle]');
      this.playlist = element.querySelector('[data-playlist]');

      this.headerPlayIcon = element.querySelector('[data-icon-play]');
      this.headerPauseIcon = element.querySelector('[data-icon-pause]');

      this.currentIndex = 0;
      this.youtubePlayer = null;
      this.isReady = false;
      this.isPlaying = false;
      this.pendingPlay = false;
      this.durationRequestToken = 0;

      this.autoplayNext =
        element.dataset.autoplayNext === 'true';

      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleToggleClick = this.handleToggleClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handlePlaylistToggle = this.handlePlaylistToggle.bind(this);

      this.init();
    }

    async init() {
      if (!this.tracks.length || !this.playerTarget) {
        this.disableControls();
        return;
      }

      this.bindEvents();
      this.updateCurrentTrackState();

      try {
        await loadYouTubeApi();
        this.createYouTubePlayer();
      } catch (error) {
        console.error(error);
        this.disableControls();
      }
    }

    bindEvents() {
      this.previousButton?.addEventListener(
        'click',
        this.handlePreviousClick
      );

      this.toggleButton?.addEventListener(
        'click',
        this.handleToggleClick
      );

      this.nextButton?.addEventListener(
        'click',
        this.handleNextClick
      );

      this.playlistToggle?.addEventListener(
        'click',
        this.handlePlaylistToggle
      );

      this.tracks.forEach((track, index) => {
        track.addEventListener('click', () => {
          if (index === this.currentIndex) {
            this.togglePlayback();
            return;
          }

          this.selectTrack(index, true);
        });
      });
    }

    createYouTubePlayer() {
      const firstVideoId = this.getVideoId(this.currentIndex);

      if (!firstVideoId) {
        this.findNextValidTrack();
        return;
      }

      this.youtubePlayer = new window.YT.Player(this.playerTarget, {
        width: 200,
        height: 200,
        videoId: firstVideoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          origin: window.location.origin
        },
        events: {
          onReady: () => {
            this.isReady = true;
            this.loadCurrentTrack(false);
            this.fetchAllDurations();
          },

          onStateChange: (event) => {
            this.handlePlayerStateChange(event);
          },

          onError: (event) => {
            this.handlePlayerError(event);
          }
        }
      });
    }

    handlePlayerStateChange(event) {
      if (!window.YT || !window.YT.PlayerState) {
        return;
      }

      switch (event.data) {
        case window.YT.PlayerState.PLAYING:
          this.setPlayingState(true);
          this.updateCurrentDuration();
          break;

        case window.YT.PlayerState.PAUSED:
        case window.YT.PlayerState.CUED:
          this.setPlayingState(false);
          break;

        case window.YT.PlayerState.ENDED:
          this.setPlayingState(false);

          if (this.autoplayNext) {
            this.selectTrack(this.getNextIndex(), true);
          }
          break;

        default:
          break;
      }
    }

    handlePlayerError(event) {
      const errorCode = event?.data;

      console.warn(
        `YouTube player error code: ${errorCode}`,
        this.getVideoId(this.currentIndex)
      );

      this.setPlayingState(false);

      const currentDuration = this.tracks[
        this.currentIndex
      ]?.querySelector('[data-track-duration]');

      if (currentDuration) {
        currentDuration.textContent = '--:--';
      }
    }

    handlePreviousClick() {
      this.selectTrack(this.getPreviousIndex(), this.isPlaying);
    }

    handleToggleClick() {
      this.togglePlayback();
    }

    handleNextClick() {
      this.selectTrack(this.getNextIndex(), this.isPlaying);
    }

    handlePlaylistToggle() {
      const isOpen = this.element.classList.toggle(
        'is-playlist-open'
      );

      if (this.playlist) {
        this.playlist.hidden = !isOpen;
      }

      if (this.playlistToggle) {
        this.playlistToggle.setAttribute(
          'aria-expanded',
          String(isOpen)
        );

        this.playlistToggle.setAttribute(
          'aria-label',
          isOpen
            ? 'プレイリストを閉じる'
            : 'プレイリストを開く'
        );
      }
    }

    togglePlayback() {
      if (!this.isReady || !this.youtubePlayer) {
        this.pendingPlay = true;
        return;
      }

      if (this.isPlaying) {
        this.youtubePlayer.pauseVideo();
      } else {
        this.youtubePlayer.playVideo();
      }
    }

    selectTrack(index, shouldPlay = false) {
      if (!this.tracks[index]) {
        return;
      }

      const videoId = this.getVideoId(index);

      if (!videoId) {
        return;
      }

      this.currentIndex = index;
      this.pendingPlay = shouldPlay;

      this.updateCurrentTrackState();
      this.loadCurrentTrack(shouldPlay);
    }

    loadCurrentTrack(shouldPlay = false) {
      if (!this.isReady || !this.youtubePlayer) {
        this.pendingPlay = shouldPlay;
        return;
      }

      const videoId = this.getVideoId(this.currentIndex);

      if (!videoId) {
        return;
      }

      this.setPlayingState(false);

      if (shouldPlay || this.pendingPlay) {
        this.youtubePlayer.loadVideoById(videoId);
      } else {
        this.youtubePlayer.cueVideoById(videoId);
      }

      this.pendingPlay = false;

      window.setTimeout(() => {
        this.updateCurrentDuration();
      }, 600);
    }

    updateCurrentTrackState() {
      this.tracks.forEach((track, index) => {
        const isCurrent = index === this.currentIndex;

        track.classList.toggle('is-current', isCurrent);
        track.setAttribute(
          'aria-current',
          isCurrent ? 'true' : 'false'
        );

        if (!isCurrent) {
          track.classList.remove('is-playing');
        }
      });
    }

    setPlayingState(isPlaying) {
      this.isPlaying = Boolean(isPlaying);

      if (this.toggleButton) {
        this.toggleButton.setAttribute(
          'aria-label',
          this.isPlaying ? '一時停止' : '再生'
        );

        this.toggleButton.setAttribute(
          'aria-pressed',
          String(this.isPlaying)
        );
      }

      if (this.headerPlayIcon) {
        this.headerPlayIcon.hidden = this.isPlaying;
      }

      if (this.headerPauseIcon) {
        this.headerPauseIcon.hidden = !this.isPlaying;
      }

      this.tracks.forEach((track, index) => {
        track.classList.toggle(
          'is-playing',
          this.isPlaying && index === this.currentIndex
        );
      });
    }

    getVideoId(index) {
      return this.tracks[index]?.dataset.videoId?.trim() || '';
    }

    getPreviousIndex() {
      return (
        this.currentIndex - 1 + this.tracks.length
      ) % this.tracks.length;
    }

    getNextIndex() {
      return (this.currentIndex + 1) % this.tracks.length;
    }

    findNextValidTrack() {
      const validIndex = this.tracks.findIndex(
        (track) => track.dataset.videoId?.trim()
      );

      if (validIndex === -1) {
        this.disableControls();
        return;
      }

      this.currentIndex = validIndex;
      this.updateCurrentTrackState();
      this.createYouTubePlayer();
    }

    updateCurrentDuration() {
      if (!this.isReady || !this.youtubePlayer) {
        return;
      }

      const duration = this.youtubePlayer.getDuration();

      if (!duration) {
        return;
      }

      const durationElement = this.tracks[
        this.currentIndex
      ]?.querySelector('[data-track-duration]');

      if (durationElement) {
        durationElement.textContent = formatDuration(duration);
      }
    }

    async fetchAllDurations() {
      if (!this.isReady || !this.youtubePlayer) {
        return;
      }

      const currentToken = ++this.durationRequestToken;
      const originalIndex = this.currentIndex;
      const originalVideoId = this.getVideoId(originalIndex);
      const wasPlaying = this.isPlaying;

      for (let index = 0; index < this.tracks.length; index += 1) {
        if (currentToken !== this.durationRequestToken) {
          return;
        }

        const videoId = this.getVideoId(index);

        if (!videoId) {
          continue;
        }

        try {
          const duration = await this.getVideoDuration(videoId);

          const durationElement = this.tracks[
            index
          ]?.querySelector('[data-track-duration]');

          if (durationElement && duration > 0) {
            durationElement.textContent = formatDuration(duration);
          }
        } catch (error) {
          console.warn(
            `曲時間を取得できませんでした: ${videoId}`,
            error
          );
        }
      }

      if (
        currentToken !== this.durationRequestToken ||
        !originalVideoId
      ) {
        return;
      }

      this.currentIndex = originalIndex;
      this.updateCurrentTrackState();

      if (wasPlaying) {
        this.youtubePlayer.loadVideoById(originalVideoId);
      } else {
        this.youtubePlayer.cueVideoById(originalVideoId);
      }
    }

    getVideoDuration(videoId) {
      return new Promise((resolve, reject) => {
        if (!this.youtubePlayer) {
          reject(new Error('プレイヤーが準備されていません。'));
          return;
        }

        let attempts = 0;
        const maxAttempts = 30;

        this.youtubePlayer.cueVideoById(videoId);

        const timer = window.setInterval(() => {
          attempts += 1;

          const duration = Number(
            this.youtubePlayer.getDuration()
          );

          if (duration > 0) {
            window.clearInterval(timer);
            resolve(duration);
            return;
          }

          if (attempts >= maxAttempts) {
            window.clearInterval(timer);
            reject(new Error('曲時間の取得がタイムアウトしました。'));
          }
        }, 150);
      });
    }

    disableControls() {
      [
        this.previousButton,
        this.toggleButton,
        this.nextButton
      ].forEach((button) => {
        if (button) {
          button.disabled = true;
          button.style.opacity = '0.35';
          button.style.cursor = 'default';
        }
      });
    }

    destroy() {
      this.previousButton?.removeEventListener(
        'click',
        this.handlePreviousClick
      );

      this.toggleButton?.removeEventListener(
        'click',
        this.handleToggleClick
      );

      this.nextButton?.removeEventListener(
        'click',
        this.handleNextClick
      );

      this.playlistToggle?.removeEventListener(
        'click',
        this.handlePlaylistToggle
      );

      if (
        this.youtubePlayer &&
        typeof this.youtubePlayer.destroy === 'function'
      ) {
        this.youtubePlayer.destroy();
      }
    }
  }

  function initializePlayers(root = document) {
    root.querySelectorAll(PLAYER_SELECTOR).forEach((element) => {
      if (element.dataset.playerInitialized === 'true') {
        return;
      }

      element.dataset.playerInitialized = 'true';
      element.customAudioPlayerInstance =
        new CustomAudioPlayer(element);
    });
  }

  function destroySectionPlayer(sectionElement) {
    const playerElement = sectionElement.querySelector(
      PLAYER_SELECTOR
    );

    if (!playerElement) {
      return;
    }

    playerElement.customAudioPlayerInstance?.destroy();
    playerElement.dataset.playerInitialized = 'false';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializePlayers();
    });
  } else {
    initializePlayers();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializePlayers(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySectionPlayer(event.target);
  });
})();